import { CommonModule, Location } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, NavigationEnd, Router, RouterModule } from '@angular/router';
import { AlertController, IonicModule, ModalController, NavController } from '@ionic/angular';
import { ColumnMode, NgxDatatableModule, SelectionType } from '@swimlane/ngx-datatable';
import * as _ from 'lodash';
import moment from 'moment';
import { NgxTippyModule } from 'ngx-tippy-wrapper';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

import { AutomationLogsComponent } from 'src/app/components/automation-logs/automation-logs.component';
import { DatetimeModalComponent } from 'src/app/components/datetime-modal/datetime-modal.component';
import { EditAutomationFunctionsComponent } from 'src/app/components/edit-automation-functions/edit-automation-functions.component';
import {
  AUTOMATION_ACTION_TYPES_ICONS,
  AUTOMATION_ACTION_TYPES_LABELS,
  DATE_OPTIONS,
  DATE_RANGE_TYPES,
  FEATURES,
  PROJECT_MEMBER_ROLES,
} from 'src/app/constants';
import { formatConversationContent, getDatesFromDateRange } from 'src/app/utils/utils';
import { createDataSource, FetchDataRequest, FetchDataResponse } from 'src/directives/paginated-datatable.directive';
import { SharedDirectivesModule } from 'src/directives/shared-directives.module';
import { Automation, AUTOMATION_ACTION_TYPES, AUTOMATION_STEP_TYPES, WhereClause } from 'src/models';
import { SharedPipesModule } from 'src/pipes/shared-pipes.module';
import { ApiService } from 'src/services/api.service';
import { AuthService } from 'src/services/auth.service';
import { AutomationAssistantService } from 'src/services/automation-assistant.service';
import { AutomationsService } from 'src/services/automations.service';
import { MessageService } from 'src/services/message.service';
import { SearchablePopoverService } from 'src/services/searchable-popover.service';

/**
 * Automations List Component
 *
 * This component manages three main tabs:
 * 1. Automations - List of automations with filtering (uses isLoading)
 * 2. Configuration - Functions, secrets, stores management (uses isLoading)
 * 3. Usage - Analytics and run history (uses isLoadingMetrics + isLoadingAutomationRuns)
 *
 * Multiple loading states are used because:
 * - Different tabs load different data from different endpoints
 * - Usage tab has two async operations with different timing
 * - This provides better UX than blocking everything with a single loader
 */
@Component({
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    IonicModule,
    NgxDatatableModule,
    SharedPipesModule,
    SharedDirectivesModule,
    RouterModule,
    NgxTippyModule,
  ],
  selector: 'app-automations-list',
  templateUrl: './automations-list.component.html',
  styleUrls: ['./automations-list.component.scss'],
})
export class AutomationsListComponent implements OnInit, OnDestroy {
  automations: Partial<Automation>[] = [];
  filteredAutomations: Partial<Automation>[] = [];
  canEditAutomations: boolean = false;

  searchText: string;
  selectedTriggerType: string = 'all';
  selectedStatus: string = 'all';

  // LOADING STATES EXPLANATION:
  // We have multiple loading states because different parts of the UI load at different times
  // and have different data dependencies. This provides better UX than a single global loader.

  // 1. General operations loader (main automations list, enabling automations, etc.)
  isLoading = false;

  // 2. Automation run logs modal loader (when viewing detailed logs for a specific run)
  isLoadingAutomationRunLogs: boolean = false;

  ColumnMode = ColumnMode;
  SelectionType = SelectionType;
  pageCount = 1;
  pageSize = 50;
  routerSub: Subscription;
  automationRunsSub: Subscription;
  automationsEnabled: boolean = false;
  featureEnabled: boolean = false;
  selectedAutomationId: string | null = null;
  selectedAutomation: Partial<Automation>;
  showAutomationAddSecretModal = false;
  automationSecrets: any[];
  automationSecret = null;
  functionsVersions: any[];
  functions: any[];
  automationStores: any[];
  showAutomationAddStoreModal = false;
  automationStore = null;
  projectId: string | null = null;

  dateRange: any = null;
  startDate: Date | null = null;
  endDate: Date | null = null;
  isDateRangeInvalid = false;

  // Dual data management
  automationMetrics: {
    totalRuns: number;
    failedRuns: number;
    successRate: number;
    lastActivity: any;
  } = {
    totalRuns: 0,
    failedRuns: 0,
    successRate: 0,
    lastActivity: null,
  };

  // Paginated table configuration
  limit = 50;
  sortField = 'startedAt';
  sortDesc = true;

  // USAGE TAB LOADING STATES:
  // The Usage tab has two separate data loading operations that happen at different times:

  // 3. Usage metrics cards loader (total runs, success rate, failed runs, last activity)
  // - Loads quickly from analytics endpoint
  // - Shows loading on the metric cards at top of Usage tab
  isLoadingMetrics = false;

  // 4. Usage automation runs table loader (paginated table of individual runs)
  // - Loads from paginated endpoint with conversation title enrichment
  // - Takes longer due to additional API calls for conversation data
  // - Shows loading spinner in the datatable itself
  // - Cleared when data subscription receives new data
  isLoadingAutomationRuns = false;

  selectedStatusFilter = 'all';
  selectedAutomationFilter: string | null = null;
  selectedAutomationName = 'All automations';

  // Enhanced tab options
  tab: 'list' | 'configuration' | 'usage' = 'list';

  // Expose constants for template use
  AUTOMATION_ACTION_TYPES_LABELS = AUTOMATION_ACTION_TYPES_LABELS;

  /**
   * Check if the automation assistant feature is enabled
   */
  get isAutomationAssistantEnabled(): boolean {
    return this.automationAssistantService.isFeatureEnabled();
  }

  constructor(
    public authService: AuthService,
    private modalCtrl: ModalController,
    private automationsService: AutomationsService,
    private automationAssistantService: AutomationAssistantService,
    private msgSrvc: MessageService,
    private router: Router,
    private route: ActivatedRoute,
    private location: Location,
    private apiService: ApiService,
    private navCtrl: NavController,
    private alertCtrl: AlertController,
    private searchablePopoverService: SearchablePopoverService,
  ) {}

  async ngOnInit() {
    this.dateRange = DATE_OPTIONS[2]; // "Last 30 Days" as default
    this.projectId = this.authService.selectedProject?.id ?? null;

    this.automationsEnabled = this.authService.tenant?.isCustomActionsEnabled;
    this.featureEnabled = !this.authService.restrictedFeatures.includes(FEATURES.AUTOMATIONS);
    this.canEditAutomations =
      this.authService.isTenantAdmin ||
      this.authService.projects.some(
        (p) => p.role === PROJECT_MEMBER_ROLES.OWNER || p.role === PROJECT_MEMBER_ROLES.AGENT,
      );

    const currentPath = this.location.path();

    this.routerSub = this.router.events
      .pipe(filter((e) => e instanceof NavigationEnd && currentPath === e.url))
      .subscribe(async () => {
        // Update project ID in case it changed
        const newProjectId = this.authService.selectedProject?.id ?? null;
        if (newProjectId !== this.projectId) {
          this.projectId = newProjectId;
          // No cache to clear since we're not caching failure data
        }

        // Use general loader for main automations list and configuration data
        this.isLoading = true;

        if (this.automationsEnabled && this.featureEnabled) {
          await Promise.all([
            this.getAutomations(), // Main automations list (for "Automations" tab)
            this.getAutomationSecrets(), // Configuration data
            this.getFunctionsVersions(), // Configuration data
            this.getAutomationStores(), // Configuration data
          ]);
        }

        this.isLoading = false;
      });

    this.route.queryParams.subscribe(async (params) => {
      if (params.view === 'configuration') {
        this.tab = 'configuration';
      } else if (params.view === 'usage') {
        this.tab = 'usage';
        if (this.automationsEnabled && this.featureEnabled) {
          // Following channels-list pattern exactly
          this.loadAutomationRuns();
          const dateValues = this.getDateRangeValues();
          this.loadMetrics(dateValues);
        }
      } else {
        this.tab = 'list';
      }
    });

    // Subscribe to automation runs data source to clear table loading state when data arrives
    // This ensures the table loading spinner is shown until actual data is received
    this.automationRunsSub = this.automationRunsDataSource.subscribe(() => {
      this.isLoadingAutomationRuns = false;
    });
  }

  ngOnDestroy() {
    this.routerSub.unsubscribe();
    this.automationRunsSub?.unsubscribe();
  }

  async getAutomations() {
    try {
      const automationsResponse = await this.automationsService.list({
        limit: null,
        order: ['startedAt'],
        descending: false,
        where: [
          {
            field: 'projectId',
            operator: '==',
            value: this.projectId,
          },
        ],
      });

      this.automations = automationsResponse.data;
      this.handleFilters();
    } catch (err) {
      this.msgSrvc.showError(err);
    }
  }

  async getFunctionsVersions() {
    try {
      const functionsVersionsResponse: any = await this.automationsService.getAllFunctionsVersions(this.projectId);

      this.functionsVersions = functionsVersionsResponse;

      const activeFunc = this.getFunctionsVersion('active');
      const functionNames = activeFunc.exportedFunctionNames || [];
      this.functions = functionNames.map((f) => {
        return {
          name: f,
          version: activeFunc.version,
        };
      });
    } catch (err) {
      this.msgSrvc.showError(err);
    }
  }

  showNextPage() {
    this.pageCount += 1;
  }

  handleFilters() {
    this.isLoadingMetrics = true;
    this.filteredAutomations = this.automations;

    // Filter by search text
    const term = this.searchText?.trim().toLowerCase();
    if (term) {
      this.filteredAutomations = this.filteredAutomations.filter(
        (automation) =>
          automation.name?.toLowerCase().includes(term) || automation.description?.toLowerCase().includes(term),
      );
    }

    // Filter by trigger type
    if (this.selectedTriggerType !== 'all') {
      this.filteredAutomations = this.filteredAutomations.filter(
        (automation) => automation.trigger?.type === this.selectedTriggerType,
      );
    }

    // Filter by status
    if (this.selectedStatus !== 'all') {
      this.filteredAutomations = this.filteredAutomations.filter(
        (automation) => automation.status === this.selectedStatus,
      );
    }
    this.isLoadingMetrics = false;
  }

  getAvailableTriggerTypes(): string[] {
    const triggerTypes = new Set<string>();
    this.automations.forEach((automation) => {
      if (automation.trigger?.type) {
        triggerTypes.add(automation.trigger.type);
      }
    });
    return Array.from(triggerTypes).sort();
  }

  getTriggerTypeLabel(type: string): string {
    const labels = {
      webhook: 'Trigger: Webhook',
      manual: 'Trigger: Manual (on button click)',
      event: 'Trigger: Automated',
      scheduled: 'Trigger: Scheduled',
      slackReaction: 'Trigger: Slack Reaction',
    };
    return labels[type] || type;
  }

  clearFilters() {
    this.searchText = '';
    this.selectedTriggerType = 'all';
    this.selectedStatus = 'all';
    this.handleFilters();
  }

  getFunctionsVersion(status: string) {
    return (this.functionsVersions ?? []).find((version) => version.status === status);
  }

  async editFunctions() {
    const customActionsVersion = this.getFunctionsVersion('draft') ?? this.getFunctionsVersion('active');

    const modal = await this.modalCtrl.create({
      component: EditAutomationFunctionsComponent,
      cssClass: 'modal-full-screen',
      componentProps: {
        version: customActionsVersion.version,
        status: customActionsVersion.status,
        projectId: this.projectId,
      },
      keyboardClose: false,
      backdropDismiss: false,
    });

    modal.present();

    modal.onDidDismiss().then(() => {
      this.getFunctionsVersions();
    });
  }

  async createAutomation(tab?: 'assistant' | 'build') {
    this.navCtrl.navigateForward(`/dashboard/automations/new${tab ? `?tab=${tab}` : ''}`);
  }

  async editAutomation(id: string) {
    if (!this.canEditAutomations) {
      this.msgSrvc.show(`You don't have permission to edit automations`);
      return;
    }

    this.navCtrl.navigateForward(`/dashboard/automations/${id}`);
  }

  async enableAutomations() {
    this.isLoading = true;

    try {
      await this.apiService.postPromise('/me/tenant/enable-custom-actions', {});

      this.automationsEnabled = true;

      await Promise.all([this.getAutomations()]);
    } catch (err) {
      this.msgSrvc.showError(err);
    }

    this.isLoading = false;
  }

  async getAutomationSecrets() {
    try {
      const secretsResponse = await this.automationsService.getSecretNames(this.projectId);

      this.automationSecrets = secretsResponse.map((secret) => {
        return {
          name: secret,
        };
      });
    } catch (err) {
      this.msgSrvc.showError(err);
    }
  }

  async getAutomationStores() {
    try {
      const storesResponse: any = await this.automationsService.getStores(this.projectId);

      this.automationStores = storesResponse;
    } catch (err) {
      this.msgSrvc.showError(err);
    }
  }

  async deleteAutomationSecret(automationSecret: any) {
    const alert = await this.alertCtrl.create({
      header: 'Are you sure you want to delete this secret?',
      buttons: [
        {
          text: 'No',
        },
        {
          text: 'Yes',
          handler: async () => {
            await this.automationsService.deleteSecret(automationSecret.name, this.projectId);
            await this.getAutomationSecrets();

            this.msgSrvc.show('Automation secret deleted successfully');
          },
        },
      ],
    });

    alert.present();
  }

  onDismissAutomationAddSecretModal() {
    this.showAutomationAddSecretModal = false;
  }

  openAddSecretModal() {
    this.automationSecret = {};
    this.showAutomationAddSecretModal = true;
  }

  async addAutomationSecret() {
    if (!this.automationSecret.name || !this.automationSecret.value) {
      this.msgSrvc.show('Please enter a name and value for the secret');
      return;
    }

    await this.automationsService.addSecret(this.automationSecret.name, this.automationSecret.value, this.projectId);
    await this.getAutomationSecrets();

    this.msgSrvc.show('Automation secret added successfully');
    this.showAutomationAddSecretModal = false;

    this.automationSecret = null;
  }

  requestAccess() {
    window.open(`mailto:sales@unthread.io?subject=Beta+Access+to+Automations`, '_blank');
  }

  async deleteAutomationStore(storeName: string) {
    const alert = await this.alertCtrl.create({
      header: 'Are you sure you want to delete this key-value store?',
      buttons: [
        {
          text: 'No',
        },
        {
          text: 'Yes',
          handler: async () => {
            await this.automationsService.deleteStore(storeName, this.projectId);
            await this.getAutomationStores();

            this.msgSrvc.show('Automation store deleted successfully');
          },
        },
      ],
    });

    alert.present();
  }

  onDismissAutomationAddStoreModal() {
    this.showAutomationAddStoreModal = false;
  }

  openAddStoreModal() {
    this.automationStore = {};
    this.showAutomationAddStoreModal = true;
  }

  async addAutomationStore() {
    if (!this.automationStore.name) {
      this.msgSrvc.show('Please enter a name and value for the store');
      return;
    }

    await this.automationsService.addStore(
      {
        name: this.automationStore.name,
        description: this.automationStore.description,
      },
      this.projectId,
    );
    await this.getAutomationStores();

    this.msgSrvc.show('Automation store added successfully');
    this.showAutomationAddStoreModal = false;

    this.automationStore = null;
  }

  formatTriggerEvent(triggerEvent: string) {
    return _.snakeCase(triggerEvent).replace(/_/g, ' ');
  }

  getStepIcon(data: any) {
    if (data.type === AUTOMATION_STEP_TYPES.BRANCHES) {
      return 'git-branch';
    }

    const type = data?.data?.action?.type;
    return AUTOMATION_ACTION_TYPES_ICONS[type] ?? 'build-outline';
  }

  getStepLabel(data: any) {
    if (data.type === AUTOMATION_STEP_TYPES.BRANCHES) {
      return 'Branching logic';
    }

    const type = data?.data?.action?.type;
    if (type === AUTOMATION_ACTION_TYPES.TRIGGER_FUNCTION && data?.data?.action?.data?.functionName) {
      return `Custom Function (${data?.data?.action?.data?.functionName})`;
    }

    return AUTOMATION_ACTION_TYPES_LABELS[type] ?? 'Custom Function';
  }

  // Run logs methods (for both success and failure)
  async viewRunLogs(run: any) {
    const modal = await this.modalCtrl.create({
      component: AutomationLogsComponent,
      componentProps: {
        run,
      },
      cssClass: 'modal-large',
    });

    await modal.present();
  }

  viewConversation(run: any) {
    const conversationId = run.input?.conversationId || run.conversationId;
    if (conversationId) {
      window.open(`/dashboard/inbox/me/conversations/${conversationId}`, '_blank');
    }
  }

  viewAutomation(failure: any) {
    if (failure.automationId) {
      window.open(`/dashboard/automations/${failure.automationId}`, '_blank');
    }
  }

  async filterByStatus() {
    this.loadUsageData();
  }

  async clearAnalyticsFilters() {
    this.selectedStatusFilter = 'all';
    this.selectedAutomationFilter = null;
    this.selectedAutomationName = 'All automations';
    this.loadUsageData();
  }

  // Refresh card data based on current filter selection
  async refreshCardData() {
    this.loadUsageData();
  }

  selectAutomationFilter(event: Event) {
    this.searchablePopoverService.automations({
      event: event,
      projectId: this.projectId,
      selectedAutomationId: this.selectedAutomationFilter,
      callback: (id: string, name: string, data: Automation) => {
        this.selectedAutomationFilter = id;
        this.selectedAutomationName = name || 'Unknown automation';
        this.loadUsageData();
      },
    });
  }

  getStatusLabel(status: string): string {
    switch (status) {
      case 'completed':
        return 'Success';
      case 'failed':
        return 'Failed';
      case 'running':
        return 'Running';
      case 'pending':
        return 'Pending';
      default:
        return status;
    }
  }

  getStatusBadgeClass(status: string): string {
    switch (status) {
      case 'completed':
        return 'status-success';
      case 'failed':
        return 'status-failed';
      case 'running':
        return 'status-running';
      case 'pending':
        return 'status-pending';
      default:
        return 'status-neutral';
    }
  }

  getTriggerType(run: any): string {
    // Extract trigger type from automation data
    return run.automation?.trigger?.type || 'unknown';
  }

  getSuccessRate(): string {
    // This method is deprecated - data is now in automationMetrics
    return this.automationMetrics.successRate.toFixed(1);
  }

  getSuccessRateClass(): string {
    const rate = this.automationMetrics.successRate;
    if (rate >= 95) return 'success';
    if (rate >= 85) return 'warning';
    return 'failure';
  }

  getLastActivityText(): string {
    // This method is deprecated - data is now in automationMetrics
    if (!this.automationMetrics.lastActivity) {
      return 'No activity';
    }
    return moment(this.automationMetrics.lastActivity.startedAt).fromNow();
  }

  selectTab(tab: 'list' | 'configuration' | 'usage') {
    this.tab = tab;
    this.navCtrl.navigateForward(this.router.url, {
      queryParams: {
        view: tab,
      },
    });
  }

  private getDateRangeValues(): { startDate: string; endDate: string } {
    if (!this.dateRange) {
      // Fallback if dateRange is not initialized
      return {
        startDate: moment().subtract(30, 'days').toISOString(),
        endDate: moment().toISOString(),
      };
    }

    if (this.dateRange.type === DATE_RANGE_TYPES.ABSOLUTE) {
      return {
        startDate: moment(this.startDate).toISOString(),
        endDate: moment(this.endDate).toISOString(),
      };
    } else {
      // Fixed logic: include current period and use consistent day-based calculation
      let startDate: moment.Moment;
      let endDate = moment(); // Include current time

      switch (this.dateRange.label) {
        case 'Last 6 Months':
          startDate = moment().subtract(6, 'months').startOf('day');
          break;
        case 'Last 12 Months':
          startDate = moment().subtract(12, 'months').startOf('day');
          break;
        case 'Last Week':
          startDate = moment().subtract(1, 'week').startOf('day');
          break;
        case 'Last Month':
          startDate = moment().subtract(1, 'month').startOf('day');
          break;
        case 'Last Year':
          startDate = moment().subtract(1, 'year').startOf('day');
          break;
        default:
          // For day-based ranges (Last 7 Days, Last 30 Days, Last 90 Days)
          // We discard the end date because this helper excludes the current day, which is intended behavior elsewhere (reporting)
          const [calcStartDate] = getDatesFromDateRange(this.dateRange);
          return {
            startDate: moment(calcStartDate).toISOString(),
            endDate: moment().toISOString(), // Include current day for automation runs
          };
      }

      return {
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
      };
    }
  }

  async showDateRangeOptions(event: Event) {
    this.searchablePopoverService.dateValues({
      event: event,
      callback: (_id: string, _name: string, object: any) => {
        this.dateRange = object;
        this.loadUsageData();
      },
    });
  }

  async changeDate(isEnd: boolean) {
    const datePickerModal = await this.modalCtrl.create({
      component: DatetimeModalComponent,
      cssClass: 'datetime-modal',
      componentProps: {
        min: isEnd ? this.startDate : null,
        selected: isEnd ? this.endDate : this.startDate,
        title: isEnd ? 'Select end date' : 'Select start date',
      },
    });

    await datePickerModal.present();

    datePickerModal.onDidDismiss().then((data) => {
      const result = data?.data?.value;
      if (result && result !== 'Invalid date') {
        const newDate = moment(result).toDate();
        if (isEnd) {
          this.endDate = newDate;
        } else {
          this.startDate = newDate;
        }
        this.isDateRangeInvalid = this.startDate.getTime() > this.endDate.getTime();
        if (!this.isDateRangeInvalid) {
          this.loadUsageData();
        }
      }
    });
  }

  getCardLabel(type: 'runs' | 'success' | 'failed' | 'activity'): string {
    switch (type) {
      case 'runs':
        return `Total Runs`;
      case 'success':
        return `Success Rate`;
      case 'failed':
        return `Failed Runs`;
      case 'activity':
        return `Last Activity`;
    }
  }

  async loadUsageData() {
    // Start loading for both metrics cards and automation runs table
    this.isLoadingMetrics = true;
    this.isLoadingAutomationRuns = true;

    const dateValues = this.getDateRangeValues();

    // Load metrics for cards (fast operation)
    await this.loadMetrics(dateValues);

    // Trigger table reload (async operation, doesn't block)
    this.automationRunsDataSource.reloadPage();

    // Metrics are done loading, clear cards loader
    this.isLoadingMetrics = false;

    // Note: isLoadingAutomationRuns will be cleared when table data arrives via subscription
    // This prevents the flash of "empty table" before data loads
  }

  fetchAutomationRuns = async ({
    limit = this.limit || 50,
    order = [this.sortField || 'startedAt'],
    descending = this.sortDesc !== undefined ? this.sortDesc : true,
    cursor,
  }: FetchDataRequest): Promise<FetchDataResponse<Partial<any>>> => {
    const dateValues = this.getDateRangeValues();
    const where: WhereClause[] = [
      { field: 'startedAt', operator: '>=', value: dateValues.startDate },
      { field: 'startedAt', operator: '<=', value: dateValues.endDate },
    ];

    // Add project filter
    if (this.projectId) {
      where.push({ field: 'projectId', operator: '==', value: this.projectId });
    }

    // Add status filter
    if (this.selectedStatusFilter !== 'all') {
      const statusValue = this.selectedStatusFilter === 'success' ? 'completed' : this.selectedStatusFilter;
      where.push({ field: 'status', operator: '==', value: statusValue });
    }

    const result = await this.automationsService.listRunsForAutomation(this.selectedAutomationFilter, {
      limit,
      order,
      descending,
      cursor,
      where,
    });
    return result;
  };

  // Assign data source after fetch function is defined (following established pattern)
  automationRunsDataSource = createDataSource(this.fetchAutomationRuns);

  // Standard paginated table loading method (following the pattern)
  async loadAutomationRuns() {
    this.isLoadingAutomationRuns = true;
    await this.automationRunsDataSource.firstPage();
    // Note: isLoadingAutomationRuns will be cleared when data arrives via subscription
  }

  // Refresh method for filter changes
  async refreshAllData() {
    await this.loadUsageData();
  }

  async loadMetrics(dateValues: { startDate: string; endDate: string }) {
    try {
      this.automationMetrics = (await this.automationsService.getAutomationMetrics(
        this.projectId,
        dateValues,
        this.selectedStatusFilter,
        this.selectedAutomationFilter,
      )) as any;
    } catch (err) {
      this.msgSrvc.showError(err);
    }
  }

  getFormattedConversationTitle(row: any): string {
    if (!row.conversation || !row.conversation.friendlyId) {
      return 'No conversation';
    }

    const friendlyId = row.conversation.friendlyId;
    const formattedTitle = formatConversationContent(row.conversation);

    return `[#${friendlyId}] ${formattedTitle}`;
  }
}
